// src/pages/dashboard/admin/pagamentosAdmin.js
import React, { useEffect, useState } from "react";
import {
    Button,
    Modal,
    Form,
    Table,
    Alert,
    Row,
    Col,
    Card,
    InputGroup,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faCheck, faTimes, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

export default function PagamentosAdmin() {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    // Data
    const [pagamentos, setPagamentos] = useState([]);
    const [praticantes, setPraticantes] = useState([]);
    const [pagamentoPraticantes, setPagamentoPraticantes] = useState([]); // all associations

    const [search, setSearch] = useState("");       // pesquisa por nome
    const [orderBy, setOrderBy] = useState("data_limite"); // ordenar por "data_limite" ou "pagamento"
    const [orderDir, setOrderDir] = useState("asc");      // ascendente/descendente
    const [showPast, setShowPast] = useState(false);      // mostrar expirados


    const GRADUACOES = [
        { value: 0, nome: "9º Kyu", className: "belt-white" },
        { value: 1, nome: "8º Kyu", className: "belt-yellow" },
        { value: 2, nome: "7º Kyu", className: "belt-orange" },
        { value: 3, nome: "6º Kyu", className: "belt-green" },
        { value: 4, nome: "5º Kyu", className: "belt-blue" },
        { value: 5, nome: "4º Kyu", className: "belt-red" },
        { value: 6, nome: "3º Kyu", className: "belt-brown" },
        { value: 7, nome: "2º Kyu", className: "belt-brown" },
        { value: 8, nome: "1º Kyu", className: "belt-brown" },
        ...Array.from({ length: 10 }, (_, i) => ({
            value: 9 + i,
            nome: `${i + 1}º Dan`,
            className: "belt-black",
        })),
    ];

    const getGraduacaoInfo = (value) =>
        GRADUACOES.find((g) => g.value === value);

    const calcularIdade = (dataNascimento) => {
        if (!dataNascimento) return null;
        const n = new Date(dataNascimento);
        if (isNaN(n)) return null;

        const hoje = new Date();
        let idade = hoje.getFullYear() - n.getFullYear();
        const m = hoje.getMonth() - n.getMonth();
        if (m < 0 || (m === 0 && hoje.getDate() < n.getDate())) idade--;
        return idade;
    };


    // UI state
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    // Create/Edit payment modal
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null); // null => creating
    const [paymentForm, setPaymentForm] = useState({ pagamento: "", valor: "", data_limite: "" });

    // Assign modal
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignPayment, setAssignPayment] = useState(null); // payment object
    const [assignList, setAssignList] = useState([]); // {praticante, checked}

    // Confirm payments modal
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmPayment, setConfirmPayment] = useState(null);
    const [confirmList, setConfirmList] = useState([]); // { assocId, praticante, isPago }

    // Search controls in modals
    const [assignSearch, setAssignSearch] = useState("");
    const [confirmSearch, setConfirmSearch] = useState("");

    // initial load
    useEffect(() => {
        loadAll();
    }, []);

    const authHeaders = () => ({ Authorization: `Bearer ${token}` });

    const loadAll = async () => {
        setLoading(true);
        setError("");
        try {
            const [resP, resPr, resPP] = await Promise.all([
                fetch(`${API_BASE_URL}/Pagamentos`, { headers: authHeaders() }),
                fetch(`${API_BASE_URL}/Praticantes`, { headers: authHeaders() }),
                fetch(`${API_BASE_URL}/PagamentoPraticantes`, { headers: authHeaders() }),
            ]);

            if (resP.ok) {
                const data = await resP.json();
                setPagamentos(Array.isArray(data) ? data : []);
            } else {
                setPagamentos([]);
            }

            if (resPr.ok) {
                const data = await resPr.json();
                // keep only ativos (based on previously used logic) - adapt if your API uses boolean or number
                const ativos = (data || []).filter((p) => p.isAtivo || p.isAtivo === 1 || p.isAtivo === true);
                setPraticantes(ativos);
            } else {
                setPraticantes([]);
            }

            if (resPP.ok) {
                const data = await resPP.json();
                setPagamentoPraticantes(Array.isArray(data) ? data : []);
            } else {
                setPagamentoPraticantes([]);
            }
        } catch (err) {
            console.error(err);
            setError("Erro ao carregar dados. Verifica a API / token.");
        } finally {
            setLoading(false);
        }
    };

    // ----------------------------
    // Payment create / edit / delete
    // ----------------------------
    const openCreatePayment = () => {
        setPaymentForm({ pagamento: "", valor: "", data_limite: "" });
        setEditingPayment(null);
        setShowPaymentModal(true);
        setError("");
        setSuccess("");
    };

    const openEditPayment = (p) => {
        setEditingPayment(p);
        setPaymentForm({
            pagamento: p.pagamento || "",
            valor: p.valor != null ? String(p.valor) : "",
            data_limite: p.data_limite ? moment(p.data_limite).format("YYYY-MM-DD") : "",
        });
        setShowPaymentModal(true);
        setError("");
        setSuccess("");
    };

    const savePayment = async (e) => {
        e && e.preventDefault && e.preventDefault();
        setError("");
        setSuccess("");
        if (!paymentForm.pagamento || !paymentForm.valor || !paymentForm.data_limite) {
            setError("Preenche todos os campos.");
            return;
        }
        const payload = {
            pagamento: paymentForm.pagamento,
            valor: parseFloat(paymentForm.valor),
            data_limite: new Date(paymentForm.data_limite).toISOString(),
        };
        try {
            let res;
            if (editingPayment) {
                res = await fetch(`${API_BASE_URL}/Pagamentos/${editingPayment.id}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json", ...authHeaders() },
                    body: JSON.stringify(payload),
                });
            } else {
                res = await fetch(`${API_BASE_URL}/Pagamentos`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...authHeaders() },
                    body: JSON.stringify(payload),
                });
            }
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || "Erro ao guardar pagamento");
            }
            setSuccess(editingPayment ? "Pagamento atualizado." : "Pagamento criado.");
            setShowPaymentModal(false);
            await loadAll();
        } catch (err) {
            console.error(err);
            setError(err.message || "Erro ao guardar pagamento.");
        }
    };

    const deletePayment = async (p) => {
        if (!window.confirm(`Deseja eliminar o pagamento "${p.pagamento}"? Todas as associações com praticantes serão também eliminadas.`)) return;

        setError("");
        setSuccess("");

        try {
            // 1. apagar todas as associações de PagamentoPraticante
            const associados = pagamentoPraticantes.filter(pp => pp.pagamentoId === p.id);
            for (const assoc of associados) {
                await fetch(`${API_BASE_URL}/pagamentopraticantes/${assoc.id}`, {
                    method: "DELETE",
                    headers: authHeaders(),
                });
            }

            // 2. apagar o pagamento
            const res = await fetch(`${API_BASE_URL}/pagamentos/${p.id}`, {
                method: "DELETE",
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error(await res.text());

            setSuccess("Pagamento eliminado.");
            await loadAll();
        } catch (err) {
            console.error(err);
            setError("Erro ao eliminar pagamento.");
        }
    };


    // ----------------------------
    // Assign payment to praticantes
    // ----------------------------
    const openAssignModal = (p) => {
        setAssignPayment(p);
        // build list: for each praticante, check if exists in pagamentoPraticantes
        const associated = pagamentoPraticantes.filter((pp) => pp.pagamentoId === p.id);
        const list = praticantes.map((pr) => ({
            praticante: pr,
            checked: !!associated.find((a) => a.praticanteId === pr.id),
        }));
        setAssignList(list);
        setAssignSearch("");
        setShowAssignModal(true);
        setError("");
        setSuccess("");
    };

    const toggleAssignCheck = (prId) => {
        setAssignList((old) => old.map((x) => (x.praticante.id === prId ? { ...x, checked: !x.checked } : x)));
    };

    const saveAssign = async () => {
        if (!assignPayment) return;
        setError("");
        setSuccess("");
        const toCreate = [];
        const novosPraticantesSms = [];
        const toDelete = [];
        // existing associations for this payment
        const existing = pagamentoPraticantes.filter((pp) => pp.pagamentoId === assignPayment.id);

        for (const item of assignList) {
            const already = pagamentoPraticantes.find(e => e.pagamentoId === assignPayment.id && e.praticanteId === item.praticante.id);
            if (item.checked && !already) {
                toCreate.push(item.praticante.id);
                novosPraticantesSms.push({
                    nome: String(item.praticante.nome),
                    telefone: String(item.praticante.telefone || "")
                });
            }
            if (!item.checked && already) {
                toDelete.push(already.id); // delete by association id
            }
        }

        try {
            // create new associations
            for (const pratId of toCreate) {
                // POST { id:0, praticanteId, pagamentoId, isPago: 0 }
                const res = await fetch(`${API_BASE_URL}/PagamentoPraticantes`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...authHeaders() },
                    body: JSON.stringify({ id: 0, praticanteId: pratId, pagamentoId: assignPayment.id, isPago: 0 }),
                });
                if (!res.ok) {
                    const txt = await res.text();
                    throw new Error(txt || "Erro ao atribuir pagamentos");
                }
            }

            // delete removed associations
            for (const assocId of toDelete) {
                const res = await fetch(`${API_BASE_URL}/PagamentoPraticantes/${assocId}`, {
                    method: "DELETE",
                    headers: authHeaders(),
                });
                if (!res.ok) {
                    const txt = await res.text();
                    throw new Error(txt || "Erro ao desatribuir pagamento");
                }
            }

            if (novosPraticantesSms.length > 0) {
                await fetch(`${API_BASE_URL}/Mensagens/atribuir-pagamento`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...authHeaders() },
                    body: JSON.stringify({
                        pagamentoId: assignPayment.id,
                        praticantes: novosPraticantesSms
                    }),
                });
            }

            setSuccess("Atribuições e avisos SMS processados.");
            setShowAssignModal(false);
            await loadAll();
        } catch (err) {
            setError("Erro ao processar atribuições.");
        }
    };

    // ----------------------------
    // Confirm payment status for assigned praticantes
    // ----------------------------
    const openConfirmModal = (p) => {
        setConfirmPayment(p);
        const associated = pagamentoPraticantes.filter((pp) => pp.pagamentoId === p.id);
        // Build list of { assocId, praticante, isPago }
        const list = associated.map((a) => {
            const prat = praticantes.find((pr) => pr.id === a.praticanteId) || { id: a.praticanteId, nome: "N/A" };
            return { assocId: a.id, praticante: prat, isPago: a.isPago === 1 || a.isPago === true };
        });
        setConfirmList(list);
        setConfirmSearch("");
        setShowConfirmModal(true);
        setError("");
        setSuccess("");
    };

    const toggleConfirmCheck = (assocId) => {
        setConfirmList((old) => old.map((x) => (x.assocId === assocId ? { ...x, isPago: !x.isPago } : x)));
    };

    const saveConfirm = async () => {
        if (!confirmPayment) return;
        setError(""); setSuccess("");
        try {
            for (const item of confirmList) {
                const assoc = pagamentoPraticantes.find((pp) => pp.id === item.assocId);
                if (!assoc) continue;

                const wasPaid = (assoc.isPago === 1 || assoc.isPago === true);
                const isNowPaid = item.isPago;

                if (wasPaid !== isNowPaid) {
                    // 1. Atualizar na BD
                    await fetch(`${API_BASE_URL}/PagamentoPraticantes/${assoc.id}`, {
                        method: "PUT",
                        headers: { "Content-Type": "application/json", ...authHeaders() },
                        body: JSON.stringify({ ...assoc, isPago: isNowPaid ? 1 : 0 }),
                    });

                    // 2. Se acabou de ser pago, enviar SMS de confirmação
                    if (isNowPaid) {
                        await fetch(`${API_BASE_URL}/Mensagens/confirmar-recebimento`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", ...authHeaders() },
                            body: JSON.stringify({
                                nomePraticante: String(item.praticante.nome),
                                telefone: String(item.praticante.telefone || ""),
                                nomePagamento: String(confirmPayment.pagamento)
                            }),
                        });
                    }
                }
            }
            setSuccess("Estados de pagamento e notificações atualizados.");
            setShowConfirmModal(false);
            await loadAll();
        } catch (err) {
            setError("Erro ao atualizar pagamentos.");
        }
    };

    // Utility: contador pagos / total
    const getCounts = (pagamentoId) => {
        const assoc = pagamentoPraticantes.filter((pp) => pp.pagamentoId === pagamentoId);
        const total = assoc.length;
        const paid = assoc.filter((a) => a.isPago === 1 || a.isPago === true).length;
        return { total, paid };
    };

    // Helpers for filtering lists in modals
    const assignFiltered = assignList.filter((x) =>
        x.praticante.nome.toLowerCase().includes(assignSearch.toLowerCase())
    );
    const confirmFiltered = confirmList.filter((x) =>
        x.praticante.nome.toLowerCase().includes(confirmSearch.toLowerCase())
    );

    // Render
    return (
        <>
            <div className="d-flex justify-content-between flex-wrap gap-3 align-items-start py-4">
                <div>
                    <h1 className="h4 mb-1">Gestão de Pagamentos</h1>
                    <p className="mb-0 text-muted">Crie, atribua e confirme pagamentos dos praticantes.</p>
                </div>
                <Button variant="primary" onClick={openCreatePayment}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Novo Pagamento
                </Button>
            </div>

            {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible>{success}</Alert>}

            {/* Filtros */}
            <Row className="mb-3 g-2 align-items-center">
                <Col xs={12} sm={5}>
                    <Form.Control placeholder="Pesquisar por nome..." value={search} onChange={e => setSearch(e.target.value)} />
                </Col>
                <Col xs={6} sm={3}>
                    <Form.Select value={orderDir} onChange={e => setOrderDir(e.target.value)}>
                        <option value="asc">Data Limite ↑</option>
                        <option value="desc">Data Limite ↓</option>
                    </Form.Select>
                </Col>
                <Col xs={6} sm={4} className="d-flex align-items-center">
                    <Form.Check type="checkbox" label="Mostrar expirados" checked={showPast} onChange={() => setShowPast(!showPast)} />
                </Col>
            </Row>

            {/* Tabela — desktop */}
            {(() => {
                const pagamentosFiltrados = pagamentos
                    .filter(p => {
                        const nomeMatch = p.pagamento?.toLowerCase().includes(search.toLowerCase());
                        const isFuture = moment(p.data_limite).isSameOrAfter(moment());
                        return nomeMatch && (showPast || isFuture);
                    })
                    .sort((a, b) => {
                        const dateA = moment(a.data_limite);
                        const dateB = moment(b.data_limite);
                        return orderDir === "asc" ? dateA - dateB : dateB - dateA;
                    });
                return (
                    <>
                        <div className="d-none d-md-block">
                            <Card className="shadow-sm mb-4">
                                <Card.Body className="p-0">
                                    <Table responsive hover className="mb-0">
                                        <thead className="thead-light">
                                        <tr>
                                            <th>Descrição</th>
                                            <th>Valor (€)</th>
                                            <th>Data Limite</th>
                                            <th>Pagos / Total</th>
                                            <th>Ações</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {pagamentosFiltrados.map((p) => {
                                            const counts = getCounts(p.id);
                                            const isExpired = moment(p.data_limite).isBefore(moment());
                                            return (
                                                <tr key={p.id} className={isExpired ? "opacity-75" : ""}>
                                                    <td className="fw-bold">{p.pagamento}</td>
                                                    <td>{Number(p.valor).toFixed(2)}</td>
                                                    <td>{p.data_limite ? moment(p.data_limite).format("DD/MM/YYYY") : "—"}</td>
                                                    <td>
                                                        <span className={`badge bg-${counts.paid === counts.total && counts.total > 0 ? "success" : "secondary"}`}>
                                                            {counts.paid} / {counts.total}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            <Button size="sm" variant="outline-primary" onClick={() => openAssignModal(p)}>Atribuir</Button>
                                                            <Button size="sm" variant="outline-success" onClick={() => openConfirmModal(p)}>Confirmar</Button>
                                                            <Button size="sm" variant="outline-secondary" onClick={() => openEditPayment(p)}><FontAwesomeIcon icon={faEdit} /></Button>
                                                            <Button size="sm" variant="outline-danger" onClick={() => deletePayment(p)}><FontAwesomeIcon icon={faTrash} /></Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </div>

                        {/* Cards — mobile */}
                        <div className="d-md-none mb-4">
                            {pagamentosFiltrados.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "48px 20px", background: "white", borderRadius: 16 }}>
                                    <p className="text-muted mb-0 fw-bold">Nenhum pagamento encontrado.</p>
                                </div>
                            ) : pagamentosFiltrados.map((p) => {
                                const counts = getCounts(p.id);
                                const isExpired = moment(p.data_limite).isBefore(moment());
                                const progresso = counts.total > 0 ? Math.round((counts.paid / counts.total) * 100) : 0;
                                return (
                                    <div key={p.id} style={{ background: "white", borderRadius: 14, padding: 16, marginBottom: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.07)", border: "1px solid #f0f0f5", opacity: isExpired ? 0.7 : 1 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                                            <div style={{ fontWeight: 700, fontSize: 15, color: "#1e1d45", flex: 1 }}>{p.pagamento}</div>
                                            <div style={{ fontWeight: 800, fontSize: 16, color: "#1e1d45", flexShrink: 0, marginLeft: 8 }}>{Number(p.valor).toFixed(2)} €</div>
                                        </div>
                                        <div style={{ fontSize: 12, color: "#6c757d", marginBottom: 10 }}>
                                            Data limite: {p.data_limite ? moment(p.data_limite).format("DD/MM/YYYY") : "—"}
                                            {isExpired && <span className="badge bg-danger ms-2" style={{ fontSize: 9 }}>Expirado</span>}
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                                            <div style={{ flex: 1, height: 6, background: "#f0f0f5", borderRadius: 3 }}>
                                                <div style={{ width: `${progresso}%`, height: "100%", background: progresso === 100 ? "#198754" : "#1e1d45", borderRadius: 3, transition: "width 0.3s" }} />
                                            </div>
                                            <span style={{ fontSize: 11, fontWeight: 600, color: progresso === 100 ? "#198754" : "#6c757d" }}>{counts.paid}/{counts.total}</span>
                                        </div>
                                        <div style={{ display: "flex", gap: 6, borderTop: "1px solid #f0f0f5", paddingTop: 10 }}>
                                            <Button size="sm" variant="outline-primary" className="flex-grow-1" onClick={() => openAssignModal(p)}>Atribuir</Button>
                                            <Button size="sm" variant="outline-success" className="flex-grow-1" onClick={() => openConfirmModal(p)}>Confirmar</Button>
                                            <Button size="sm" variant="outline-secondary" onClick={() => openEditPayment(p)}><FontAwesomeIcon icon={faEdit} /></Button>
                                            <Button size="sm" variant="outline-danger" onClick={() => deletePayment(p)}><FontAwesomeIcon icon={faTrash} /></Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                );
            })()}
    {/* ------------------------
          Create / Edit Payment Modal
         ------------------------ */}
            <Modal show={showPaymentModal} centered onHide={() => setShowPaymentModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingPayment ? "Editar Pagamento" : "Novo Pagamento"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={savePayment}>
                        <Form.Group className="mb-3">
                            <Form.Label>Descrição</Form.Label>
                            <Form.Control
                                type="text"
                                value={paymentForm.pagamento}
                                onChange={(e) => setPaymentForm({ ...paymentForm, pagamento: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Valor (€)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={paymentForm.valor}
                                        onChange={(e) => setPaymentForm({ ...paymentForm, valor: e.target.value })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Data Limite</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={paymentForm.data_limite}
                                        onChange={(e) => setPaymentForm({ ...paymentForm, data_limite: e.target.value })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" className="me-2" onClick={() => setShowPaymentModal(false)}>
                                Cancelar
                            </Button>
                            <Button variant="primary" type="submit">
                                Guardar
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Modal Atribuir */}
            <Modal size="lg" show={showAssignModal} centered onHide={() => setShowAssignModal(false)} scrollable>
                <Modal.Header closeButton>
                    <div>
                        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>Atribuir Pagamento</Modal.Title>
                        <div style={{ fontSize: 12, color: "#6c757d" }}>{assignPayment?.pagamento}</div>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ background: "#f8f9fa", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#495057" }}>
                        Selecione os praticantes que devem efetuar este pagamento.
                    </div>
                    <Form.Control className="mb-3" placeholder="Pesquisar praticante..." value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)} />
                    <div onClick={() => {
                        const allChecked = assignFiltered.every(x => x.checked);
                        setAssignList(old => old.map(x => assignFiltered.some(f => f.praticante.id === x.praticante.id) ? { ...x, checked: !allChecked } : x));
                    }} style={{ display: "flex", alignItems: "center", padding: "10px 14px", background: "#f0f0f5", borderRadius: 10, marginBottom: 8, cursor: "pointer" }}>
                        <div style={{ width: 28, height: 28, borderRadius: 6, border: `2px solid ${assignFiltered.length > 0 && assignFiltered.every(x => x.checked) ? "#1e1d45" : "#dee2e6"}`, background: assignFiltered.length > 0 && assignFiltered.every(x => x.checked) ? "#1e1d45" : "white", display: "flex", alignItems: "center", justifyContent: "center", marginRight: 12, flexShrink: 0 }}>
                            {assignFiltered.length > 0 && assignFiltered.every(x => x.checked) && <span style={{ color: "white", fontSize: 14 }}>✓</span>}
                        </div>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>Selecionar todos ({assignFiltered.length})</span>
                    </div>
                    {assignFiltered.map((item) => {
                        const p = item.praticante;
                        const g = getGraduacaoInfo(p.graduacao);
                        return (
                            <label key={p.id} style={{ display: "flex", alignItems: "center", padding: "12px 14px", background: "white", borderRadius: 12, marginBottom: 6, cursor: "pointer", border: item.checked ? "2px solid #1e1d45" : "1px solid #e9ecef", minHeight: 64 }}>
                                <div style={{ width: 28, height: 28, borderRadius: 6, border: `2px solid ${item.checked ? "#1e1d45" : "#dee2e6"}`, background: item.checked ? "#1e1d45" : "white", display: "flex", alignItems: "center", justifyContent: "center", marginRight: 12, flexShrink: 0 }}>
                                    {item.checked && <span style={{ color: "white", fontSize: 14 }}>✓</span>}
                                </div>
                                <input type="checkbox" checked={!!item.checked} onChange={() => toggleAssignCheck(p.id)} style={{ display: "none" }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: 14 }}>{p.nome}</div>
                                    <div style={{ display: "flex", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
                                        {g && <span className={`belt-badge ${g.className}`} style={{ fontSize: 10 }}>{g.nome}</span>}
                                        {calcularIdade(p.dataNascimento) && <span style={{ fontSize: 12, color: "#6c757d" }}>{calcularIdade(p.dataNascimento)} anos</span>}
                                    </div>
                                </div>
                            </label>
                        );
                    })}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" className="flex-grow-1 flex-sm-grow-0" onClick={() => setShowAssignModal(false)}>Cancelar</Button>
                    <Button variant="primary" className="flex-grow-1 flex-sm-grow-0" onClick={saveAssign}>Guardar Atribuições</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Confirmar Pagamentos */}
            <Modal size="lg" show={showConfirmModal} centered onHide={() => setShowConfirmModal(false)} scrollable>
                <Modal.Header closeButton>
                    <div>
                        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>Confirmar Pagamentos</Modal.Title>
                        <div style={{ fontSize: 12, color: "#6c757d" }}>{confirmPayment?.pagamento}</div>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 14 }}>
                        {[
                            { label: "Pagos", value: confirmList.filter(x => x.isPago).length, color: "#198754" },
                            { label: "Por pagar", value: confirmList.filter(x => !x.isPago).length, color: "#dc3545" },
                            { label: "Total", value: confirmList.length, color: "#1e1d45" },
                        ].map(s => (
                            <div key={s.label} style={{ background: "#f8f9fa", borderRadius: 10, padding: "10px 8px", textAlign: "center", borderTop: `3px solid ${s.color}` }}>
                                <div style={{ fontSize: 20, fontWeight: 900, color: s.color }}>{s.value}</div>
                                <div style={{ fontSize: 10, color: "#6c757d", fontWeight: 600 }}>{s.label}</div>
                            </div>
                        ))}
                    </div>
                    <Form.Control className="mb-3" placeholder="Pesquisar praticante..." value={confirmSearch} onChange={(e) => setConfirmSearch(e.target.value)} />
                    {confirmFiltered.map((item) => {
                        const p = item.praticante;
                        const g = getGraduacaoInfo(p.graduacao);
                        return (
                            <label key={item.assocId} style={{ display: "flex", alignItems: "center", padding: "12px 14px", background: "white", borderRadius: 12, marginBottom: 6, cursor: "pointer", border: item.isPago ? "2px solid #198754" : "1px solid #e9ecef", minHeight: 64 }}>
                                <div style={{ width: 28, height: 28, borderRadius: 6, border: `2px solid ${item.isPago ? "#198754" : "#dee2e6"}`, background: item.isPago ? "#198754" : "white", display: "flex", alignItems: "center", justifyContent: "center", marginRight: 12, flexShrink: 0 }}>
                                    {item.isPago && <span style={{ color: "white", fontSize: 14 }}>✓</span>}
                                </div>
                                <input type="checkbox" checked={!!item.isPago} onChange={() => toggleConfirmCheck(item.assocId)} style={{ display: "none" }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: 14 }}>{p.nome}</div>
                                    <div style={{ display: "flex", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
                                        {g && <span className={`belt-badge ${g.className}`} style={{ fontSize: 10 }}>{g.nome}</span>}
                                        <span className={`badge ${item.isPago ? "bg-success" : "bg-secondary"}`} style={{ fontSize: 10 }}>
                                            {item.isPago ? "Pago" : "Pendente"}
                                        </span>
                                    </div>
                                </div>
                            </label>
                        );
                    })}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" className="flex-grow-1 flex-sm-grow-0" onClick={() => setShowConfirmModal(false)}>Cancelar</Button>
                    <Button variant="primary" className="flex-grow-1 flex-sm-grow-0" onClick={saveConfirm}>Guardar Estados</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
